// The Sales portal's data layer (/sales; Zooylo staff only).
//
// Kept apart from the shared PortalDataSource on purpose: everything the Sales
// portal reads or writes goes through this one interface, which has a Supabase
// implementation (the sales_* RPCs of migration 20261023000014 plus the
// role-checked console_* RPCs of 0008/0009) and an in-memory demo one.
//
// THE LOAD-BEARING CONTRACT is the RPC argument names: PostgREST matches named
// arguments, so every key below must match the SQL byte for byte.
// sales.test.ts parses the migrations and checks them.
//
// The server checks every role (app_platform_can); the UI hides what a role
// cannot do with `roleCan`, which mirrors the SQL. No child data here.

import type { PostgrestError, SupabaseClient } from "@supabase/supabase-js";
import { getSupabaseClient } from "../lib/supabase";
import { PortalWriteError } from "./supabaseAdapter";
import { shouldUseSupabase } from "./index";
import type { Invoice, InvoicePreview, InvoiceStatus, NewAccessCode, OrgKind, OrgLifecycle, LicenceTerms } from "./types";
import { createDemoSalesDataSource } from "./salesDemo";

// ---- Roles --------------------------------------------------------------------

export const SALES_ROLES = ["owner", "sales", "finance", "support"] as const;
export type SalesRole = (typeof SALES_ROLES)[number];

/** What a role may change. Reading is open to every Zooylo role. */
export type SalesAction = "accounts" | "pipeline" | "codes" | "agreements" | "org_status" | "invoices" | "billing" | "team";

const ROLE_CAN: Record<SalesRole, readonly SalesAction[]> = {
  owner: ["accounts", "pipeline", "codes", "agreements", "org_status", "invoices", "billing", "team"],
  sales: ["accounts", "pipeline", "codes", "agreements", "org_status"],
  finance: ["invoices", "billing", "org_status"],
  support: [],
};

/** Mirrors app_platform_can (0014); "team" is owner-only (sales_set_role). */
export function roleCan(role: SalesRole | null | undefined, action: SalesAction): boolean {
  return !!role && ROLE_CAN[role].includes(action);
}

export function toRole(v: unknown): SalesRole {
  return (SALES_ROLES as readonly string[]).includes(String(v)) ? (v as SalesRole) : "support";
}

// ---- Shapes -------------------------------------------------------------------

export interface SalesMe {
  role: SalesRole;
  email: string;
}

export interface SalesAccount {
  orgId: string;
  name: string;
  kind: OrgKind;
  status: OrgLifecycle;
  createdAt: string;
  legalName: string | null;
  registrationNo: string | null;
  country: string;
  billingAddress: string | null;
  taxId: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  salesOwner: string | null;
  source: string | null;
  notes: string | null;
  adminEmails: string[];
  familyAllowance: number;
  licencesLive: number;
  licencesClaimed: number;
  familyPriceMonthly: number;
  familyRetailPriceMonthly: number;
  sstRate: number;
  currency: string;
  paymentTermsDays: number;
  agreementNotes: string | null;
  contractStart: string | null;
  contractEnd: string | null;
  billingEmail: string | null;
  outstanding: number;
}

/** sales_save_account's keys. An absent key is left alone; null clears it. */
export interface AccountFields {
  legal_name?: string | null;
  registration_no?: string | null;
  country?: string | null;
  billing_address?: string | null;
  tax_id?: string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  sales_owner?: string | null;
  source?: string | null;
  notes?: string | null;
  payment_terms_days?: number;
  agreement_notes?: string | null;
}

export const PROSPECT_STAGES = ["lead", "qualified", "demo", "proposal", "negotiating", "won", "lost"] as const;
export type ProspectStage = (typeof PROSPECT_STAGES)[number];

export interface Prospect {
  id: string;
  name: string;
  kind: OrgKind;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  stage: ProspectStage;
  expectedLicences: number | null;
  nextAction: string | null;
  nextActionOn: string | null;
  lostReason: string | null;
  notes: string | null;
  orgId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** sales_save_prospect's keys. */
export interface ProspectFields {
  name?: string;
  kind?: OrgKind;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  stage?: ProspectStage;
  expected_licences?: number | null;
  next_action?: string | null;
  next_action_on?: string | null;
  lost_reason?: string | null;
  notes?: string | null;
  org_id?: string | null;
}

/** sales_onboarding's keys, in the order a new customer reaches them. */
export const ONBOARDING_STEPS = [
  "org_created",
  "admin_signed_in",
  "first_staff_invited",
  "first_licence",
  "first_family_signed_in",
  "first_child_shared",
  "first_invoice",
] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];
/** Each step's date, or null when it has not happened yet. */
export type Onboarding = Record<OnboardingStep, string | null>;

export const AGE_BUCKETS = ["current", "1-30", "31-60", "61-90", "90+"] as const;
export type AgeBucket = (typeof AGE_BUCKETS)[number];

export interface Receivable {
  invoiceId: string;
  orgId: string;
  orgName: string;
  number: string;
  amount: number;
  currency: string;
  issuedAt: string;
  dueAt: string | null;
  daysOverdue: number;
  ageBucket: AgeBucket;
}

export interface BillingPreviewRow {
  orgId: string;
  orgName: string;
  status: OrgLifecycle;
  licenceMonths: number;
  unitPrice: number;
  subtotal: number;
  sstAmount: number;
  amount: number;
  currency: string;
  alreadyInvoiced: boolean;
}

export interface SalesReports {
  currency: string;
  mrr: number;
  organisationsActive: number;
  organisationsSuspended: number;
  licencesLive: number;
  licencesClaimed: number;
  receivables: number;
  paidLast90Days: number;
  licenceMonthsByMonth: { month: string; licenceMonths: number }[];
  funnel: Record<ProspectStage, number>;
}

export interface AuditEntry {
  id: string;
  actorEmail: string | null;
  entity: string;
  entityId: string;
  action: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  at: string;
}

export interface TeamMember {
  accountId: string;
  email: string;
  role: SalesRole;
  addedAt: string | null;
}

export interface SalesAccessCode {
  code: string;
  label: string;
  kind: OrgKind;
  seatsTotal: number;
  familyAllowance: number;
  familyPriceMonthly: number;
  familyRetailPriceMonthly: number;
  sstRate: number;
  currency: string;
  maxUses: number;
  uses: number;
  expiresAt: string | null;
  createdAt: string;
}

export interface IssueInvoiceInput {
  periodStart: string;
  periodEnd: string;
  /** null → the server computes licence-months × price. */
  amount: number | null;
  due: string | null;
  note: string | null;
}

export interface SalesDataSource {
  /** The signed-in person's Zooylo role, or null for anyone not on the team. */
  me(): Promise<SalesMe | null>;
  accounts(): Promise<SalesAccount[]>;
  saveAccount(orgId: string, fields: AccountFields): Promise<void>;
  prospects(): Promise<Prospect[]>;
  saveProspect(id: string | null, fields: ProspectFields): Promise<string>;
  onboarding(orgId: string): Promise<Onboarding>;
  receivables(): Promise<Receivable[]>;
  /** `month` is any day of the month, "YYYY-MM-DD". */
  billingPreview(month: string): Promise<BillingPreviewRow[]>;
  issueMonth(month: string): Promise<number>;
  reports(): Promise<SalesReports>;
  auditLog(orgId: string | null): Promise<AuditEntry[]>;
  team(): Promise<TeamMember[]>;
  /** null removes the person from the Zooylo team. */
  setRole(accountId: string, role: SalesRole | null): Promise<void>;
  addTeamMember(email: string, role: SalesRole): Promise<string>;
  accessCodes(): Promise<SalesAccessCode[]>;
  createAccessCode(input: NewAccessCode): Promise<string>;
  setLicence(orgId: string, terms: LicenceTerms): Promise<void>;
  setOrgStatus(orgId: string, status: OrgLifecycle, endLicences: boolean): Promise<void>;
  invoices(orgId: string): Promise<Invoice[]>;
  previewInvoice(orgId: string, periodStart: string, periodEnd: string): Promise<InvoicePreview | null>;
  issueInvoice(orgId: string, input: IssueInvoiceInput): Promise<string>;
  setInvoiceStatus(invoiceId: string, status: InvoiceStatus): Promise<void>;
}

// ---- Row mapping ----------------------------------------------------------------

export function num(v: unknown): number {
  const n = typeof v === "number" ? v : typeof v === "string" ? Number(v) : 0;
  return Number.isFinite(n) ? n : 0;
}
const str = (v: unknown): string | null => (typeof v === "string" && v !== "" ? v : null);
const date = (v: unknown): string | null => (typeof v === "string" && v.length >= 10 ? v.slice(0, 10) : null);
const kind = (v: unknown): OrgKind => (v === "individual" ? "individual" : "organisation");
const lifecycle = (v: unknown): OrgLifecycle => (v === "suspended" || v === "closed" ? v : "active");
const stage = (v: unknown): ProspectStage =>
  (PROSPECT_STAGES as readonly string[]).includes(String(v)) ? (v as ProspectStage) : "lead";
const bucket = (v: unknown): AgeBucket => ((AGE_BUCKETS as readonly string[]).includes(String(v)) ? (v as AgeBucket) : "current");

type Row = Record<string, unknown>;

export const toSalesAccount = (r: Row): SalesAccount => ({
  orgId: String(r.org_id),
  name: String(r.name ?? ""),
  kind: kind(r.kind),
  status: lifecycle(r.status),
  createdAt: String(r.created_at ?? ""),
  legalName: str(r.legal_name),
  registrationNo: str(r.registration_no),
  country: str(r.country) ?? "MY",
  billingAddress: str(r.billing_address),
  taxId: str(r.tax_id),
  contactName: str(r.contact_name),
  contactEmail: str(r.contact_email),
  contactPhone: str(r.contact_phone),
  salesOwner: str(r.sales_owner),
  source: str(r.source),
  notes: str(r.notes),
  adminEmails: Array.isArray(r.admin_emails) ? (r.admin_emails as string[]) : [],
  familyAllowance: num(r.family_allowance),
  licencesLive: num(r.licences_live),
  licencesClaimed: num(r.licences_claimed),
  familyPriceMonthly: num(r.family_price_monthly),
  familyRetailPriceMonthly: num(r.family_retail_price_monthly),
  sstRate: num(r.sst_rate),
  currency: str(r.currency) ?? "MYR",
  paymentTermsDays: r.payment_terms_days == null ? 30 : num(r.payment_terms_days),
  agreementNotes: str(r.agreement_notes),
  contractStart: date(r.contract_start),
  contractEnd: date(r.contract_end),
  billingEmail: str(r.billing_email),
  outstanding: num(r.outstanding),
});

export const toProspect = (r: Row): Prospect => ({
  id: String(r.id),
  name: String(r.name ?? ""),
  kind: kind(r.kind),
  contactName: str(r.contact_name),
  contactEmail: str(r.contact_email),
  contactPhone: str(r.contact_phone),
  stage: stage(r.stage),
  expectedLicences: r.expected_licences == null ? null : num(r.expected_licences),
  nextAction: str(r.next_action),
  nextActionOn: date(r.next_action_on),
  lostReason: str(r.lost_reason),
  notes: str(r.notes),
  orgId: str(r.org_id),
  createdAt: String(r.created_at ?? ""),
  updatedAt: String(r.updated_at ?? ""),
});

export function toOnboarding(v: Row | null): Onboarding {
  const out = {} as Onboarding;
  for (const k of ONBOARDING_STEPS) out[k] = str(v?.[k]);
  return out;
}

export const toReceivable = (r: Row): Receivable => ({
  invoiceId: String(r.invoice_id),
  orgId: String(r.org_id),
  orgName: String(r.org_name ?? ""),
  number: String(r.number ?? ""),
  amount: num(r.amount),
  currency: str(r.currency) ?? "MYR",
  issuedAt: String(r.issued_at ?? ""),
  dueAt: date(r.due_at),
  daysOverdue: num(r.days_overdue),
  ageBucket: bucket(r.age_bucket),
});

export const toBillingPreviewRow = (r: Row): BillingPreviewRow => ({
  orgId: String(r.org_id),
  orgName: String(r.org_name ?? ""),
  status: lifecycle(r.status),
  licenceMonths: num(r.licence_months),
  unitPrice: num(r.unit_price),
  subtotal: num(r.subtotal),
  sstAmount: num(r.sst_amount),
  amount: num(r.amount),
  currency: str(r.currency) ?? "MYR",
  alreadyInvoiced: r.already_invoiced === true,
});

export function toReports(v: Row | null): SalesReports {
  const funnel = {} as Record<ProspectStage, number>;
  const f = (v?.funnel ?? {}) as Row;
  for (const s of PROSPECT_STAGES) funnel[s] = num(f[s]);
  const months = Array.isArray(v?.licence_months_by_month) ? (v!.licence_months_by_month as Row[]) : [];
  return {
    currency: str(v?.currency) ?? "MYR",
    mrr: num(v?.mrr),
    organisationsActive: num(v?.organisations_active),
    organisationsSuspended: num(v?.organisations_suspended),
    licencesLive: num(v?.licences_live),
    licencesClaimed: num(v?.licences_claimed),
    receivables: num(v?.receivables),
    paidLast90Days: num(v?.paid_last_90_days),
    licenceMonthsByMonth: months.map((m) => ({ month: String(m.month ?? ""), licenceMonths: num(m.licence_months) })),
    funnel,
  };
}

const obj = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;

export const toAuditEntry = (r: Row): AuditEntry => ({
  id: String(r.id),
  actorEmail: str(r.actor_email),
  entity: String(r.entity ?? ""),
  entityId: String(r.entity_id ?? ""),
  action: String(r.action ?? ""),
  before: obj(r.before),
  after: obj(r.after),
  at: String(r.at ?? ""),
});

export const toTeamMember = (r: Row): TeamMember => ({
  accountId: String(r.account_id),
  email: String(r.email ?? ""),
  role: toRole(r.role),
  addedAt: str(r.added_at),
});

export const toSalesAccessCode = (r: Row): SalesAccessCode => ({
  code: String(r.code ?? ""),
  label: String(r.label ?? ""),
  kind: kind(r.kind),
  seatsTotal: num(r.seats_total),
  familyAllowance: num(r.family_allowance),
  familyPriceMonthly: num(r.family_price_monthly),
  familyRetailPriceMonthly: num(r.family_retail_price_monthly),
  sstRate: num(r.sst_rate),
  currency: str(r.currency) ?? "MYR",
  maxUses: num(r.max_uses),
  uses: num(r.uses),
  expiresAt: str(r.expires_at),
  createdAt: String(r.created_at ?? ""),
});

const toInvoice = (r: Row): Invoice => ({
  id: String(r.id),
  orgId: String(r.org_id),
  number: String(r.number ?? ""),
  periodStart: date(r.period_start) ?? "",
  periodEnd: date(r.period_end) ?? "",
  licenceMonths: num(r.licence_months),
  unitPrice: num(r.unit_price),
  subtotal: r.subtotal == null ? num(r.amount) : num(r.subtotal),
  sstRate: num(r.sst_rate),
  sstAmount: num(r.sst_amount),
  amount: num(r.amount),
  currency: str(r.currency) ?? "MYR",
  status: (["draft", "issued", "paid", "void"].includes(String(r.status)) ? r.status : "issued") as InvoiceStatus,
  issuedAt: String(r.issued_at ?? ""),
  dueAt: date(r.due_at),
  paidAt: str(r.paid_at),
  note: str(r.note),
});

/** Columns read from `invoice` for one account. */
export const SALES_INVOICE_COLUMNS =
  "id, org_id, number, period_start, period_end, licence_months, unit_price, subtotal, sst_rate, sst_amount, amount, currency, status, issued_at, due_at, paid_at, note";

// ---- Supabase ---------------------------------------------------------------------

function rpcError(op: string, error: PostgrestError): PortalWriteError {
  return new PortalWriteError(op, error.message, error.code);
}

async function callRpc<T>(client: SupabaseClient, fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await client.rpc(fn, args);
  if (error) throw rpcError(fn, error);
  return data as T;
}

const rows = (v: unknown): Row[] => (Array.isArray(v) ? (v as Row[]) : []);

export function createSupabaseSalesDataSource(client: SupabaseClient): SalesDataSource {
  return {
    me: async () => {
      try {
        const r = rows(await callRpc<unknown>(client, "sales_me", {}))[0];
        return r ? { role: toRole(r.role), email: String(r.email ?? "") } : null;
      } catch {
        // Not on the team, no session, no function yet: the portal stays hidden.
        return null;
      }
    },
    accounts: async () => rows(await callRpc<unknown>(client, "sales_accounts", {})).map(toSalesAccount),
    saveAccount: (orgId, fields) => callRpc<void>(client, "sales_save_account", { p_org: orgId, p_fields: fields }),
    prospects: async () => rows(await callRpc<unknown>(client, "sales_prospects", {})).map(toProspect),
    saveProspect: (id, fields) => callRpc<string>(client, "sales_save_prospect", { p_id: id, p_fields: fields }),
    onboarding: async (orgId) => toOnboarding(obj(await callRpc<unknown>(client, "sales_onboarding", { p_org: orgId }))),
    receivables: async () => rows(await callRpc<unknown>(client, "sales_receivables", {})).map(toReceivable),
    billingPreview: async (month) =>
      rows(await callRpc<unknown>(client, "sales_billing_preview", { p_month: month })).map(toBillingPreviewRow),
    issueMonth: async (month) => num(await callRpc<unknown>(client, "sales_issue_month", { p_month: month })),
    reports: async () => toReports(obj(await callRpc<unknown>(client, "sales_reports", {}))),
    auditLog: async (orgId) => rows(await callRpc<unknown>(client, "sales_audit_log", { p_org: orgId })).map(toAuditEntry),
    team: async () => rows(await callRpc<unknown>(client, "sales_team", {})).map(toTeamMember),
    setRole: (accountId, role) => callRpc<void>(client, "sales_set_role", { p_account: accountId, p_role: role }),
    addTeamMember: (email, role) => callRpc<string>(client, "sales_add_team_member", { p_email: email, p_role: role }),
    accessCodes: async () => rows(await callRpc<unknown>(client, "console_access_codes", {})).map(toSalesAccessCode),
    createAccessCode: (input) =>
      callRpc<string>(client, "console_create_access_code", {
        p_label: input.label,
        p_kind: input.kind,
        p_seats: input.seats,
        p_family_allowance: input.familyAllowance,
        p_family_price: input.familyPrice,
        p_family_retail_price: input.familyRetailPrice,
        p_sst_rate: input.sstRate,
        p_currency: input.currency,
        p_max_uses: input.maxUses,
        p_expires_at: input.expiresAt,
      }),
    setLicence: (orgId, terms) =>
      callRpc<void>(client, "console_set_licence", {
        p_org: orgId,
        p_seats: terms.seats,
        p_family_allowance: terms.familyAllowance,
        p_family_price: terms.familyPrice,
        p_family_retail_price: terms.familyRetailPrice,
        p_sst_rate: terms.sstRate,
        p_currency: terms.currency,
        p_billing_email: terms.billingEmail,
        p_contract_end: terms.contractEnd,
      }),
    setOrgStatus: (orgId, status, endLicences) =>
      callRpc<void>(client, "console_set_org_status", { p_org: orgId, p_status: status, p_end_licences: endLicences }),
    invoices: async (orgId) => {
      const { data, error } = await client
        .from("invoice")
        .select(SALES_INVOICE_COLUMNS)
        .eq("org_id", orgId)
        .order("period_start", { ascending: false });
      if (error) throw rpcError("invoices", error);
      return rows(data).map(toInvoice);
    },
    previewInvoice: async (orgId, periodStart, periodEnd) => {
      const r = rows(
        await callRpc<unknown>(client, "console_preview_invoice", {
          p_org: orgId,
          p_period_start: periodStart,
          p_period_end: periodEnd,
        }),
      )[0];
      if (!r) return null;
      return {
        licenceMonths: num(r.licence_months),
        unitPrice: num(r.unit_price),
        subtotal: r.subtotal == null ? num(r.amount) : num(r.subtotal),
        sstRate: num(r.sst_rate),
        sstAmount: num(r.sst_amount),
        amount: num(r.amount),
        currency: str(r.currency) ?? "MYR",
      };
    },
    issueInvoice: (orgId, input) =>
      callRpc<string>(client, "console_issue_invoice", {
        p_org: orgId,
        p_period_start: input.periodStart,
        p_period_end: input.periodEnd,
        p_amount: input.amount,
        p_due: input.due,
        p_note: input.note,
      }),
    setInvoiceStatus: (invoiceId, status) =>
      callRpc<void>(client, "console_set_invoice_status", { p_invoice: invoiceId, p_status: status }),
  };
}

// ---- Selection ---------------------------------------------------------------------

let _sales: SalesDataSource | null = null;

/** The Supabase source when the portal is configured for it, else the demo. */
export function getSalesData(): SalesDataSource {
  if (_sales) return _sales;
  _sales = shouldUseSupabase() ? createSupabaseSalesDataSource(getSupabaseClient()) : createDemoSalesDataSource();
  return _sales;
}

/** For unit tests only. */
export function _resetSalesDataForTest(): void {
  _sales = null;
}

// ---- Pure helpers the screens share ----------------------------------------------------

/** RFC 4180 CSV. A cell that a spreadsheet would run as a formula is quoted inert. */
export function toCsv(table: (string | number | null | undefined)[][]): string {
  return table
    .map((row) =>
      row
        .map((cell) => {
          let s = cell == null ? "" : String(cell);
          if (/^[=+\-@\t\r]/.test(s) && !/^-?\d+(\.\d+)?$/.test(s)) s = `'${s}`;
          return /[",\n\r]/.test(s) || s !== s.trim() ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(","),
    )
    .join("\r\n");
}

/** Fields the audit view never lists: bookkeeping, not a change anyone made. */
const AUDIT_SKIP = new Set(["id", "org_id", "created_at", "updated_at", "status_changed_at"]);

export interface AuditChange {
  field: string;
  before: unknown;
  after: unknown;
}

/**
 * What a change actually changed: on an update, each field whose value differs;
 * on an insert, each field it set; on a delete, each field it held.
 */
export function auditChanges(e: Pick<AuditEntry, "before" | "after">): AuditChange[] {
  const keys = new Set([...Object.keys(e.before ?? {}), ...Object.keys(e.after ?? {})]);
  const out: AuditChange[] = [];
  for (const k of keys) {
    if (AUDIT_SKIP.has(k)) continue;
    const b = e.before?.[k] ?? null;
    const a = e.after?.[k] ?? null;
    if (JSON.stringify(b) === JSON.stringify(a)) continue;
    out.push({ field: k, before: b, after: a });
  }
  return out;
}

/** The previous calendar month as "YYYY-MM" (UTC). */
export function previousMonth(nowMs: number = Date.now()): string {
  const d = new Date(nowMs);
  const m = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, 1));
  return m.toISOString().slice(0, 7);
}
